import { Order, TunnelWashStatus } from '../models/order.models';
import {
  TUNNEL_COMPLETED_LINGER_MS,
  TUNNEL_WASH_NO_CONFIRMATION_MS,
} from '../constants/orders.constants';

/**
 * Tunnel wash display rules for the tunnel board (CWS-1921, contract §3–§4).
 * Pure: the board passes the clock (`nowMs`) and a date formatter.
 */

export type TunnelWashFields = Pick<
  Order,
  'tunnelWashStatus' | 'tunnelPackageName' | 'tunnelWashTriggeredAt' | 'tunnelCompletedAt'
>;

/** How a list row is drawn: grey "sent", neutral "pending", or a red notice. */
export type TunnelWashRowTone = 'sent' | 'pending' | 'unconfirmed' | 'error';

export interface TunnelWashRowDisplay {
  tone: TunnelWashRowTone;
  text: string;
  /** Seconds before a sent row leaves the list; null for other states. */
  removeInSeconds: number | null;
  /** Accessible label of that countdown; null when there is none. */
  removeInLabel: string | null;
}

export interface TunnelWashDetailDisplay {
  packageName: string;
  statusLabel: string;
  tone: TunnelWashRowTone;
  /** Formatted time of the state (fired / triggered); empty when unknown. */
  timeLabel: string;
  /** Plain explanation for staff; empty when the state needs none. */
  explanation: string;
}

export interface TunnelWashStateEvent {
  lprEntryId: string;
  status: TunnelWashStatus | null;
  packageName: string | null;
  completedAt: string | null;
  /** Server time the wash was triggered (CWS-1920 event field); null on older servers. */
  triggeredAt: string | null;
  locationId: string | null;
}

const STATUSES: readonly TunnelWashStatus[] = ['pending', 'sent', 'failed', 'no_tunnel_package'];

export const TUNNEL_WASH_NOT_LINKED_NOTICE =
  'Not linked to a tunnel wash package — start the wash manually';
export const TUNNEL_WASH_FAILED_NOTICE = 'Tunnel trigger failed — start the wash manually';
export const TUNNEL_WASH_SENDING_TEXT = 'Sending wash…';
export const TUNNEL_WASH_UNCONFIRMED_TEXT = 'No confirmation from the tunnel controller';

export function parseTunnelWashStatus(raw: unknown): TunnelWashStatus | null {
  return STATUSES.find(status => status === raw) ?? null;
}

function toMs(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function cleanString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const text = String(value).trim();
  return text ? text : null;
}

/** Tunnel wash fields of a `get-all-orders` row. */
export function mapTunnelWashFieldsFromApi(row: Record<string, unknown>): TunnelWashFields {
  return {
    tunnelWashStatus: parseTunnelWashStatus(row['tunnel_wash_status']),
    tunnelPackageName: cleanString(row['tunnel_package_name']),
    tunnelWashTriggeredAt: cleanString(row['tunnel_wash_triggered_at']),
    tunnelCompletedAt: cleanString(row['tunnel_completed_at']),
  };
}

/** Milliseconds a `sent` row has left on the board; null when it does not linger. */
export function tunnelWashRemainingMs(order: TunnelWashFields, nowMs: number): number | null {
  if (order.tunnelWashStatus !== 'sent') {
    return null;
  }
  const completedMs = toMs(order.tunnelCompletedAt);
  if (completedMs === null) {
    return null;
  }
  return completedMs + TUNNEL_COMPLETED_LINGER_MS - nowMs;
}

/** A `sent` row whose linger time is over (client timer, and on reload). */
export function isTunnelWashExpired(order: TunnelWashFields, nowMs: number): boolean {
  const remaining = tunnelWashRemainingMs(order, nowMs);
  return remaining !== null && remaining <= 0;
}

/** `pending` for longer than the confirmation window. */
export function isTunnelWashUnconfirmed(order: TunnelWashFields, nowMs: number): boolean {
  if (order.tunnelWashStatus !== 'pending') {
    return false;
  }
  const triggeredMs = toMs(order.tunnelWashTriggeredAt);
  return triggeredMs !== null && nowMs - triggeredMs > TUNNEL_WASH_NO_CONFIRMATION_MS;
}

/** Whether the board needs its 1 s clock (a countdown or a pending wash on screen). */
export function needsTunnelWashClock(orders: TunnelWashFields[]): boolean {
  return orders.some(
    order => order.tunnelWashStatus === 'sent' || order.tunnelWashStatus === 'pending'
  );
}

function noCountdown(tone: TunnelWashRowTone, text: string): TunnelWashRowDisplay {
  return { tone, text, removeInSeconds: null, removeInLabel: null };
}

/**
 * Where the selection goes when rows leave the list: the index, among the kept rows,
 * of the first row kept after `selectedIndex` (i.e. how many rows before it stay).
 * Clamped to the last kept row; -1 when nothing is kept.
 */
export function indexAfterRemoval<T>(
  rows: readonly T[],
  selectedIndex: number,
  isKept: (row: T) => boolean
): number {
  const kept = rows.filter(isKept).length;
  if (kept === 0) {
    return -1;
  }
  const keptBefore = rows.slice(0, Math.max(0, selectedIndex)).filter(isKept).length;
  return Math.min(keptBefore, kept - 1);
}

export function describeTunnelWashRow(
  order: TunnelWashFields,
  nowMs: number
): TunnelWashRowDisplay | null {
  switch (order.tunnelWashStatus) {
    case 'sent': {
      const remaining = tunnelWashRemainingMs(order, nowMs);
      const seconds = remaining === null ? null : Math.max(0, Math.ceil(remaining / 1000));
      return {
        tone: 'sent',
        text: order.tunnelPackageName ? `Wash sent · ${order.tunnelPackageName}` : 'Wash sent',
        removeInSeconds: seconds,
        removeInLabel: seconds === null ? null : `Leaves the list in ${seconds} seconds`,
      };
    }
    case 'pending':
      return isTunnelWashUnconfirmed(order, nowMs)
        ? noCountdown('unconfirmed', TUNNEL_WASH_UNCONFIRMED_TEXT)
        : noCountdown('pending', TUNNEL_WASH_SENDING_TEXT);
    case 'failed':
      return noCountdown('error', TUNNEL_WASH_FAILED_NOTICE);
    case 'no_tunnel_package':
      return noCountdown('error', TUNNEL_WASH_NOT_LINKED_NOTICE);
    default:
      return null;
  }
}

/** Right panel / details dialog block; null when the row has no tunnel wash state. */
export function describeTunnelWashDetail(
  order: TunnelWashFields & Pick<Order, 'packageName'>,
  nowMs: number,
  formatTime: (iso: string) => string
): TunnelWashDetailDisplay | null {
  const status = order.tunnelWashStatus;
  if (!status) {
    return null;
  }
  const packageName = order.tunnelPackageName || '—';
  const triggeredLabel = order.tunnelWashTriggeredAt ? formatTime(order.tunnelWashTriggeredAt) : '';
  switch (status) {
    case 'sent':
      return {
        packageName,
        statusLabel: 'Wash sent',
        tone: 'sent',
        timeLabel: order.tunnelCompletedAt ? formatTime(order.tunnelCompletedAt) : triggeredLabel,
        explanation: '',
      };
    case 'pending': {
      const unconfirmed = isTunnelWashUnconfirmed(order, nowMs);
      return {
        packageName,
        statusLabel: unconfirmed ? TUNNEL_WASH_UNCONFIRMED_TEXT : TUNNEL_WASH_SENDING_TEXT,
        tone: unconfirmed ? 'unconfirmed' : 'pending',
        timeLabel: triggeredLabel,
        explanation: unconfirmed
          ? 'The tunnel controller has not confirmed the wash. Check the tunnel and start the wash manually if it did not start.'
          : '',
      };
    }
    case 'failed':
      return {
        packageName,
        statusLabel: 'Trigger failed',
        tone: 'error',
        timeLabel: triggeredLabel,
        explanation: 'The tunnel controller could not start the wash. Start the wash manually.',
      };
    case 'no_tunnel_package': {
      const name = order.packageName?.trim();
      return {
        packageName: '—',
        statusLabel: 'Not linked',
        tone: 'error',
        timeLabel: triggeredLabel,
        explanation: name
          ? `The package ${name} has no tunnel wash package at this location. Start the wash manually.`
          : 'This package has no tunnel wash package at this location. Start the wash manually.',
      };
    }
  }
}

/** Reads a `tunnel_wash_state` payload; null when it names no board row. */
export function parseTunnelWashStateEvent(data: unknown): TunnelWashStateEvent | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const payload = data as Record<string, unknown>;
  const lprEntryId = cleanString(payload['lpr_entry_id']);
  if (!lprEntryId) {
    return null;
  }
  return {
    lprEntryId,
    status: parseTunnelWashStatus(payload['tunnel_wash_status']),
    packageName: cleanString(payload['tunnel_package_name']),
    completedAt: cleanString(payload['tunnel_completed_at']),
    triggeredAt: cleanString(payload['tunnel_wash_triggered_at']),
    locationId: cleanString(payload['location_id'] ?? payload['locationId']),
  };
}

/**
 * A row after a `tunnel_wash_state` event. The 15 s confirmation window runs from the
 * server's trigger time; a row turning `pending` without one is stamped with the client clock.
 */
export function applyTunnelWashState<T extends TunnelWashFields>(
  order: T,
  event: TunnelWashStateEvent,
  nowMs: number
): T {
  const becamePending = event.status === 'pending' && order.tunnelWashStatus !== 'pending';
  return {
    ...order,
    tunnelWashStatus: event.status,
    tunnelPackageName: event.packageName ?? order.tunnelPackageName ?? null,
    // A `sent` event without a time still has to leave the board after the linger.
    tunnelCompletedAt:
      event.status === 'sent'
        ? (event.completedAt ?? order.tunnelCompletedAt ?? new Date(nowMs).toISOString())
        : (event.completedAt ?? null),
    tunnelWashTriggeredAt:
      event.triggeredAt ??
      (becamePending ? new Date(nowMs).toISOString() : (order.tunnelWashTriggeredAt ?? null)),
  };
}

/** A `tunnel_wash_state` event tagged with the newest list request already sent when it arrived. */
export interface RecordedTunnelWashEvent {
  afterFetchSeq: number;
  event: TunnelWashStateEvent;
}

/**
 * Re-applies, in arrival order, the events that arrived after list request `fetchSeq` was
 * sent: that response may predate them, and must not revert a newer state.
 */
export function reapplyTunnelWashEvents<T extends TunnelWashFields & Pick<Order, 'id' | 'lprId'>>(
  rows: T[],
  recorded: RecordedTunnelWashEvent[],
  fetchSeq: number,
  nowMs: number
): T[] {
  const newer = recorded.filter(entry => entry.afterFetchSeq >= fetchSeq);
  if (!newer.length) {
    return rows;
  }
  return rows.map(row => {
    let current = row;
    for (const { event } of newer) {
      if (row.lprId === event.lprEntryId || row.id === event.lprEntryId) {
        current = applyTunnelWashState(current, event, nowMs);
      }
    }
    return current;
  });
}
